/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState } from 'react';
import {
  IReturnShipments,
  setUpdatedShipmentFields,
  useReturnStore,
} from '@src/stores/returnStore';
import { RETURN_METHOD } from '@src/constants/general';
import {
  Typography,
  Checkbox,
  Link,
  Alert,
} from '@one-thd/sui-atomic-components';
import { formatPhoneNumber } from '@src/utils/general';
import { ItemToReturn } from '@src/models/returns';
import ReturnReviewCard from '../shared/summary-cards/ReturnReviewCard';
import ReviewItemDetails from './ReviewItemDetails';
import LORPContactDetails from '../lorp/lorp-contact-details/LORPContactDetails';
import TermsAndConditionsDrawer from './user-acknowledge/TermsAndConditionsDrawer';
import { PhoneNumberDrawer } from '../lorp/PhoneNumberDrawer';
import { ILORPContactDetails } from '../lorp/lorp-contact-details/LORPContactDetailsStep';

interface ReturnDetailCardProps {
  lorpShipments: IReturnShipments[];
  onEditItemSubmit: (
    startingState: ItemToReturn,
    closeDrawer: () => void,
  ) => void;
  onRemoveItemSubmit: (selectedItemId: string) => void;
  isPolicyValidatorLoading: boolean;
  showLorpCheckboxError: boolean;
}

export const LORPGroupedReturnDetailCard = ({
  lorpShipments,
  onEditItemSubmit,
  onRemoveItemSubmit,
  isPolicyValidatorLoading,
  showLorpCheckboxError,
}: ReturnDetailCardProps) => {
  const [displayTermsDrawer, setDisplayTermsDrawer] = useState(false);
  const selectedItems = useReturnStore((state) => state.selectedItems);
  const [displayPhoneDrawer, setDisplayPhoneDrawer] = useState(false);
  const { hasAgreed, lorpContactDetails } = lorpShipments?.[0] || {};
  const lorpShipmentIds = lorpShipments.map((shipment) => shipment.shipmentId);
  const lorpLineItems = selectedItems.filter(
    (item) => item.returnMethod === RETURN_METHOD.LORP,
  );

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    // Update checkmark status for selected shipment
    setUpdatedShipmentFields(lorpShipmentIds, {
      hasAgreed: checked,
    });
  };

  const acceptTermsBtnClick = () => {
    setUpdatedShipmentFields(lorpShipmentIds, {
      hasAgreed: true,
    });
  };

  const updateContactDetails = (updatedContactDetails: ILORPContactDetails) =>
    setUpdatedShipmentFields(lorpShipmentIds, {
      lorpContactDetails: updatedContactDetails,
    });
  const handlePhoneSave = (phoneNumber: string) =>
    updateContactDetails({
      ...lorpContactDetails,
      phoneNumber,
    });

  return (
    <ReturnReviewCard header="Return Pickup">
      {/* Items List */}
      <div className="sui-space-y-6">
        {lorpShipments.map((shipment, index) => {
          return shipment.lineItems.map((item) => {
            const itemToReturn = lorpLineItems.find(
              (lineItem) => lineItem.lineItemId === item.lineItemId,
            );
            if (!itemToReturn) return null;
            return (
              <div
                key={`shipment-${shipment.shipmentId}-item-${item.lineItemId}`}
                className="sui-flex sui-items-start sui-gap-4"
              >
                <ReviewItemDetails
                  item={itemToReturn}
                  index={index}
                  onEditItemSubmit={onEditItemSubmit}
                  onRemoveItemSubmit={onRemoveItemSubmit}
                  isPolicyValidatorLoading={isPolicyValidatorLoading}
                />
              </div>
            );
          });
        })}
      </div>

      <LORPContactDetails
        contactDetails={lorpContactDetails}
        setDisplayPhoneDrawer={setDisplayPhoneDrawer}
      />

      {/* Terms and Conditions Checkbox */}
      <div className="sui-mt-6">
        <hr />
        {showLorpCheckboxError && (
          <div data-testid="lorp-checkbox-error" className="sui-mt-6">
            <Alert status="error">
              <Typography variant="body-sm">
                Acknowledge the terms and conditions.
              </Typography>
            </Alert>
          </div>
        )}
        <div
          className={`sui-flex ${
            showLorpCheckboxError ? 'sui-mt-2' : 'sui-mt-6'
          }`}
        >
          <div className="sui-flex sui-items-center">
            <Checkbox
              id={`lorp-agreement-${lorpShipmentIds.join('-')}`}
              checked={hasAgreed ?? false}
              onChange={handleCheckboxChange}
              inputAttributes={{
                'aria-label': 'lorp-agreement-checkbox',
              }}
            />
            <Typography height="tight">
              <label
                htmlFor={`lorp-agreement-${lorpShipmentIds.join('-')}`}
                className="sui-text-base sui-leading-5 sui-cursor-pointer"
              >
                I have read and acknowledge the terms and conditions.
              </label>
            </Typography>
          </div>
          <div className="sui-ml-auto">
            <Link onClick={() => setDisplayTermsDrawer(true)}>View</Link>
          </div>
        </div>
      </div>
      <TermsAndConditionsDrawer
        closeDrawer={() => setDisplayTermsDrawer(false)}
        displayDrawer={displayTermsDrawer}
        acceptTerms={acceptTermsBtnClick}
      />
      <PhoneNumberDrawer
        open={displayPhoneDrawer}
        onClose={() => setDisplayPhoneDrawer(false)}
        onSave={handlePhoneSave}
        initialPhoneNumber={formatPhoneNumber(lorpContactDetails?.phoneNumber)}
      />
    </ReturnReviewCard>
  );
};
